/**
 * Section 22 — End-to-End Test Harness.
 *
 * In-depth documentation of the /e2e-tests page: the data collection
 * mechanisms, the methodology, what the harness CAN and CANNOT verify,
 * and how to use it as a pre-deploy gate.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import GuideCallout from '../GuideCallout';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const SubH = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const Code = ({ children }: { children: React.ReactNode }) => (
  <Typography component="code" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.85rem', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>{children}</Typography>
);

const Section_e2eTests: GuideSection = {
  id: 'e2e-tests',
  label: 'End-to-End Tests',
  group: 'reference',
  icon: <ScienceIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>End-to-End Test Harness</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A complete reference for the admin-only <Code>/e2e-tests</Code> page: what it tests, how it collects data, how reliable the
        results are, and when to trust them as a deployment gate.
      </Typography>

      <H id="purpose">Purpose</H>
      <P>
        The harness is a deterministic, button-triggered suite of <strong>46 scenarios</strong> that exercise the production code
        responsible for trade decisions, indicator math, voter classifications, settings validation, trade-lifecycle state
        transitions, and the AutoTrade safety guards. Run via the <strong>Run All Tests</strong> button. Results stream live,
        persist for the last 20 runs, and can be diffed scenario-by-scenario against any previous run.
      </P>
      <P>
        The harness is built on the principle that every BUY/SHORT decision the platform makes ultimately comes from a small set of
        well-defined formulas and rules. If those formulas survive a regression check after every code change, the platform's
        scoring, signals, and trade-lifecycle decisions stay correct.
      </P>

      <H id="data-collection">Data collection mechanism — how every scenario gets its inputs</H>
      <P>
        There are two distinct data-collection mechanisms inside the harness, used in different proportions:
      </P>

      <SubH>1. Hand-computed fixtures (39 of 46 scenarios — the "pure-rule" set)</SubH>
      <P>
        These scenarios fabricate inputs in-line in the scenario file and feed them to the production function under test. No
        database, no broker, no network, no clock dependency. Examples:
      </P>
      <List dense sx={{ pl: 2, mb: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="L6 (Auto-ISL/Target LONG) — builds a 30-bar synthetic uptrend OHLC fixture, calls computeAutoIslTarget() with N=2, asserts |target − entry| = N × |entry − ISL| within float precision." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="CPR1 (Central Pivot Range) — passes a 2-day candle fixture with Day 1 H=120, L=80, C=110 to computeCpr(), asserts pivot=(120+80+110)/3, bc=(120+80)/2, tc=pivot+(pivot−bc)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="W1 (WR% R116b zone classifier) — walks a 9-case truth table covering BUY zone, SHORT zone, mismatch NEUTRAL, flat-fast NEUTRAL, slow==mid boundary, and three NaN warm-up guards." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="SMA1 (DailyBlast SMA) — includes an explicit Round-70 regression test (100 leading nulls + 10 flat values) to lock in the fix for a real historical bug that pushed CMid into millions." />
        </ListItem>
      </List>
      <GuideCallout kind="info">
        Hand-computed fixtures give the strongest possible verification: the expected output is derived independently from the
        same formula in plain mathematics. If the production code's output diverges, the test fails immediately at the offending
        boundary input.
      </GuideCallout>

      <SubH>2. Real DB writes + mock broker (7 of 46 scenarios — the "lifecycle &amp; guard" set)</SubH>
      <P>
        These scenarios test the trade-lifecycle pipeline and the order-safety guards by writing real rows to the production
        Postgres database and invoking actual production methods (the trade monitor, the trailing-SL manager, the manual-execute
        guard, the order gate, and the no-stacking guard). To do this safely, the harness:
      </P>
      <List dense sx={{ pl: 2, mb: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Seeds a sentinel user — system+e2e@earngush.com — with role='user', status='suspended', isActive=false. The user cannot authenticate; only the harness writes against this id." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary='Seeds a synthetic Delta instrument — E2E_BTCUSD — with a name distinct from any real Delta symbol the user trades, so it cannot accidentally collide with live data.' />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary='Inserts Trade rows tagged with isSimulation=true so any production scan that filters non-simulation trades automatically skips them.' />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary='Constructs a MockDeltaAdapter that satisfies the same interface as DeltaExchangeService. The mock logs every call into a callLog so the scenario can assert "broker received order X" or "broker received NO order". Real Delta API calls are NEVER made.' />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary='Cleans up — every scenario deletes its trade row in a finally block so the database returns to a known-good state, even if the test fails or throws mid-run.' />
        </ListItem>
      </List>
      <GuideCallout kind="warning">
        Lifecycle scenarios run against the live Postgres on production. They are SAFE because they isolate to the E2E user id
        and never call real Delta. But a manual <Code>SELECT * FROM trades WHERE userId = 7</Code> while a run is in flight will
        show transient mock rows.
      </GuideCallout>

      <H id="mechanism">The runner mechanism — what happens when you click Run All Tests</H>
      <P>
        The orchestrator (<Code>backend/src/services/e2e/runOrchestrator.ts</Code>) handles the full lifecycle. A click triggers
        these steps:
      </P>
      <Box component="ol" sx={{ pl: 3, mb: 2 }}>
        <li><P><strong>POST /v1/e2e/run</strong> hits the admin-only endpoint. Auth is verified via JWT + admin role check.</P></li>
        <li><P>A new row is inserted into <Code>e2e_test_runs</Code> with status=<Code>running</Code>, returning a UUID.</P></li>
        <li><P>The orchestrator iterates the registered <Code>SCENARIOS</Code> array sequentially (in plan order). Each scenario gets a fresh <Code>ScenarioContext</Code> with the structured <Code>ctx.expect()</Code> assertion API.</P></li>
        <li><P>For each scenario, a 40 ms delay is inserted after every step emit (so the UI can paint the log line). A 250 ms delay is inserted between scenarios (so PASS chips are visible before the next row starts).</P></li>
        <li><P>Each scenario's result row is persisted in <Code>e2e_scenario_results</Code> as soon as it finishes, with status, duration, failure reason (if any), and the full step log.</P></li>
        <li><P>Live events are broadcast over WebSocket to the user's socket room. The UI also polls <Code>GET /v1/e2e/runs/:id</Code> every 1 second as a fallback — both paths converge on the same state.</P></li>
        <li><P>When the loop finishes, the run row gets <Code>status=passed</Code> (all scenarios pass) or <Code>status=failed</Code> (any fails), plus aggregate counts.</P></li>
        <li><P>The runner prunes any runs older than the 20-run retention window (cascade-deletes the related scenario rows).</P></li>
      </Box>

      <H id="reliability">Reliability — what the harness CAN and CANNOT trust</H>

      <SubH>What you CAN trust the harness verifies</SubH>
      <List dense sx={{ pl: 2, mb: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Every formula tested has its expected output hand-computed from the underlying math. A mismatch by a single floating-point ε is a real failure, not a false alarm." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Boundary inputs are walked explicitly: less-than, equal-to, greater-than the threshold; warm-up bars; degenerate inputs (zero, NaN, null, empty arrays); inputs at the upper and lower clamp limits of every setting." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Positive AND negative cases — for every rule, the suite includes both 'this MUST fire' inputs and 'this MUST NOT fire' inputs. A regression that flips the comparison direction is caught immediately." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Historical regressions explicitly locked in — e.g. SMA1 includes the Round-70 regression fixture that protects the past CMid-into-millions bug from re-emerging." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Cross-import bugs (e.g. accidentally using one EMA implementation where another was intended) — locked in by the E3 scenario which verifies the 3 different EMA variants behave correctly during warm-up and converge to identical post-seed values." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Structural invariants — e.g. transformCandles preserving bar count and time stamps so downstream indicators are not silently misaligned." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="MFE / R-multiple / TSL activation / snooze freezing — verified against real production trade-monitor code paths, not synthetic re-implementations." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="AutoTrade safety guards — AE1 drives the production order gate to prove protective exits bypass the kill switch + daily-loss cap (a position is always closeable) while entries are still blocked; AE2 drives the production no-stacking guard to prove one-position-per-symbol on both the scan and webhook entry paths (OPEN/DRAFT block, CLOSED frees the symbol)." />
        </ListItem>
      </List>

      <SubH>What the harness CANNOT (yet) verify</SubH>
      <List dense sx={{ pl: 2, mb: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Real Delta Exchange order placement. The mock broker echoes back synthetic order IDs and fills. Real-broker behavior under rate limits, partial fills, gap-throughs of stops, and connection drops is NOT tested." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Historical-candle-driven indicators (TSL HA-band computation, ATR-based spike-mode triggers) for the mock instrument E2E_BTCUSD, which has no candle history seeded. The math for these is verified in isolation via pure-rule scenarios, but the integration over real candle data is not." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Frontend rendering. The React components that paint OverView strips, RockBand bands, the Strategies signal table, etc. are not directly tested by this suite." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Webhook routing — the full TradingView webhook → backend payload-validation → alert-generation HTTP chain. Tested in production by sending real (test) webhooks. (The webhook's no-stacking guard logic IS covered in isolation by AE2.)" />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Concurrent user scenarios (race conditions when 10 users have an open trade simultaneously, multiple alerts firing for the same symbol within a polling window, etc.)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="Long-running trade evolution (multi-day price journeys, end-of-day square-off, weekend gaps, futures roll-over)." />
        </ListItem>
      </List>

      <H id="deploy-gate">Using the harness as a deployment gate</H>

      <P>
        The E2E suite is a <strong>necessary but not sufficient</strong> gate. A green run is required before a production deploy
        but does not on its own guarantee the deploy will not regress something the suite doesn't cover.
      </P>

      <SubH>Recommended pre-deploy checklist</SubH>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Step</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>What it gives you</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><strong>1. Run E2E suite</strong></TableCell>
            <TableCell>All-pass = no regression to any covered math, rule, or state-transition. Failures tell you exactly which rule broke and why.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>2. Diff against previous run</strong></TableCell>
            <TableCell>If the previous run was green, the Diff view confirms zero scenarios changed status. Any "added" or "removed" scenario should be intentional.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>3. Manual smoke test in staging</strong></TableCell>
            <TableCell>Open the changed feature on staging, exercise the happy path, verify the UI updates as expected. Catches frontend regressions the suite cannot.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>4. Deploy with a rollback path</strong></TableCell>
            <TableCell>Push the new build. Keep the previous Docker image tagged so a rollback is one <Code>docker compose up</Code> away.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>5. Monitor for 24 hours</strong></TableCell>
            <TableCell>Watch Logs (page or container) and Telegram alerts. The first day after a deploy is when integration regressions usually surface.</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <SubH>Trust levels by change type</SubH>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Change type</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Trust E2E green = safe to deploy?</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Indicator math tweak (WR%, JHM, RSI, EMA, etc.)</TableCell>
            <TableCell><strong>High.</strong> The pure-rule scenarios catch any boundary or value regression.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Settings clamp / validation change</TableCell>
            <TableCell><strong>High.</strong> T3 walks every clamp boundary.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>OverView / RockBand score formula change</TableCell>
            <TableCell><strong>High.</strong> O1/O2/O3 verify the central scoring chain end-to-end.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Auto-ISL / Target / breach-buffer formula change</TableCell>
            <TableCell><strong>High.</strong> L6/L7/L8/S3 verify the production computeAutoIslTarget directly.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Trade-monitor MFE / TSL activation / snooze change</TableCell>
            <TableCell><strong>Medium-High.</strong> LC1/LC2/LC3 cover the state transitions. Real candle-driven TSL computation is NOT covered.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Order gate / AutoTrade guards (kill switch, daily-loss cap, no-stacking)</TableCell>
            <TableCell><strong>High.</strong> AE1/AE2 drive the production gate + no-stacking guard directly across their full truth tables.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Broker integration (DeltaExchangeService order placement)</TableCell>
            <TableCell><strong>Low.</strong> Mock broker doesn't exercise real Delta. Manually test in Delta sandbox or with a tiny live order.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Frontend UI / React component / routing change</TableCell>
            <TableCell><strong>Low.</strong> Not covered. Manual smoke test required.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Database migration</TableCell>
            <TableCell><strong>Medium.</strong> The suite will fail loudly if a migration breaks a model. But a no-op migration that succeeds silently can still drift schema.</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <H id="ci">CI integration</H>
      <P>
        A GitHub Actions workflow (<Code>.github/workflows/e2e.yml</Code>) runs the same suite in headless mode (<Code>npm run e2e:headless</Code> in the
        backend directory) on every push to master and every pull request. Failures fail the build. The workflow uploads a JSON
        report as a 14-day artifact for after-the-fact diffing. This means E2E coverage is enforced before merge, not just before
        deploy — regressions caught at PR time are easier to fix than regressions caught in production.
      </P>

      <H id="adding-scenarios">Adding new scenarios</H>
      <P>
        Every new feature with non-trivial math or state-transition logic should ship with at least one E2E scenario covering it.
        The pattern:
      </P>
      <Box component="ol" sx={{ pl: 3, mb: 2 }}>
        <li><P>If the rule is inline in a larger service, lift it verbatim to <Code>backend/src/services/e2e/rules/&lt;name&gt;.ts</Code> with a one-line comment pointing to the original source line.</P></li>
        <li><P>Write a scenario file in <Code>backend/src/services/e2e/scenarios/&lt;id&gt;_&lt;descriptor&gt;.ts</Code> using the existing <Code>ScenarioContext</Code> API.</P></li>
        <li><P>Register the scenario in <Code>scenarioRegistry.ts</Code> with a plain-English <Code>description</Code> and a <Code>productionPath</Code> pointing at the source file/line.</P></li>
        <li><P>Typecheck (<Code>npx tsc --noEmit</Code>), commit, push. The GitHub Actions workflow will run the suite on the next CI tick.</P></li>
      </Box>

      <H id="limits">Known limits + roadmap</H>
      <P>
        The harness was designed in <Code>docs/e2e-trade-cycle-test-plan.md</Code> with a target of ~205 scenarios across 8
        implementation stages. The current 46 cover Stages 1, 2, 3 (partial), 4 (partial), 7, and 8, plus the AutoTrade safety
        guards. The remaining unblock would require:
      </P>
      <List dense sx={{ pl: 2, mb: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="A ScriptedLtpStream so each scenario can drive its own price series for the trade-monitor loop, unlocking spike-mode / profit-lock-tier / gap-through-SL coverage." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="A VirtualClock so time-dependent rules (breach hold windows, EOD square-off, snooze expiry) can be exercised in milliseconds instead of waiting wall-clock minutes." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyle: 'disc', py: 0.25 }}>
          <ListItemText primary="A SeededCandleSource that injects candle history per scenario, unlocking real candle-driven TSL ratchet and spike-mode tests." />
        </ListItem>
      </List>
      <GuideCallout kind="tip">
        The 46 scenarios already cover the math and decision-rule surface that drives every BUY/SHORT, every Auto-ISL/Target, every
        OverView score, every Settings-clamp guard, every Risk-Cockpit state, the AutoTrade exit-safety + no-stacking guards, and
        the core trade-lifecycle state transitions (MFE, activation, snooze). Future scenarios extend depth and breadth — they
        don't fix coverage gaps in what already passes.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'End-to-End Tests',                anchor: 'purpose',           body: 'e2e tests harness scenarios admin run all tests' },
    { heading: 'Data collection mechanism',       anchor: 'data-collection',   body: 'hand-computed fixture mock broker E2E_USER E2E_BTCUSD isSimulation trade row' },
    { heading: 'Runner mechanism',                anchor: 'mechanism',         body: 'orchestrator websocket polling run history scenario context' },
    { heading: 'Reliability',                     anchor: 'reliability',       body: 'reliable trust pre-deploy gate what is covered what is not' },
    { heading: 'Deploy gate',                     anchor: 'deploy-gate',       body: 'pre-deploy checklist staging rollback trust by change type' },
    { heading: 'CI integration',                  anchor: 'ci',                body: 'github actions npm run e2e:headless pull request workflow' },
    { heading: 'Adding scenarios',                anchor: 'adding-scenarios',  body: 'how to add new e2e scenario rule helper registry productionPath' },
    { heading: 'Known limits',                    anchor: 'limits',            body: 'scripted ltp virtual clock seeded candle source roadmap' },
  ],
};

export default Section_e2eTests;
